// Shared types and contracts for Good-time Casino.
// Mirrors the server's domain models so the mobile app can consume them type-safely.

import type { ItemId } from './items';
import type { Beat, BoardRunState, CollectState, MorningLights, PlayKind, PlayOffer, RoadState } from './road';

export * from './road';
export * from './realm';

export type PlayerId = string & { readonly __brand: 'PlayerId' };
export type SessionId = string & { readonly __brand: 'SessionId' };
export type GameRoomId = string & { readonly __brand: 'GameRoomId' };
export type WalletId = string & { readonly __brand: 'WalletId' };
export type ScoreId = string & { readonly __brand: 'ScoreId' };

export type GameType = 'slot' | 'bingo' | 'wheel_of_fortune';

export interface Player {
  id: PlayerId;
  displayName: string;
  email?: string | null;
  handle?: string | null;
  /** Onboarding curriculum step (1..5). New anonymous players land
   * at 1; advances server-side as completion criteria fire (UX §4). */
  onboardingSession: number;
  created: string;
  /** Feature flags on for this player (the server's FeatureService). Absent
   *  on an older server; read through {@code useFeatures}. */
  features?: string[];
  /** For each of those features that runs on some slots only (Royal Road),
   *  the slots it runs on for this player: `{"royal_road": ["forest",
   *  "royal"]}`. An empty list means no slot. Absent on an older server. */
  featureThemes?: Record<string, string[]>;
  /** The player's secret, sent back as {@code X-Player-Token}. Only in the
   *  response that creates the player. */
  token?: string | null;
}

export interface GameRoom {
  id: GameRoomId;
  gameType: GameType;
  themeId: string;
  stateVersion: number;
  created: string;
  /** Social MVP — true for a friends-only private room. */
  isPrivate?: boolean;
  /** Social MVP — shareable join code (e.g. "GTC-4F2A"); null/absent
   *  on public rooms. Present in the create response for private
   *  rooms so the host can display + share it. */
  inviteCode?: string | null;
}

export interface Wallet {
  id: WalletId;
  playerId: PlayerId;
  coins: number;
  gems: number;
  /** T1 (2026-05-28) — slot ids the player has purchased with gems.
   *  Free-by-default slots aren't here; derived from
   *  {@code FREE_SLOT_NODE_IDS}. */
  unlockedSlotIds?: readonly string[];
  /** B' (2026-05-31) — epoch-ms of the most recent
   *  {@code zero_balance_refill} wallet_delta for this player, or
   *  {@code null} if the player has never been refilled. The mobile
   *  client compares against the last value it saw and fires the
   *  {@code RefillBurst} overlay on advance. */
  lastRefillAtMs?: number | null;
  /** Cumulative coins wagered (paid slot spins + wheel-room bets). The
   *  player level derives from it, and the server credits the level-up gems
   *  as it crosses each level. Absent from servers before 2026-10-06. */
  xp?: number;
  created: string;
}

/** {@code POST /v1/players/{id}/wallet/daily-bonus}. {@code claimed} is
 *  false when the player already claimed in the last 24 hours: nothing was
 *  credited, and {@code streak} / {@code claimedAt} describe that claim. */
export interface DailyBonusResponse {
  claimed: boolean;
  /** Coins credited by this claim; 0 when {@code claimed} is false. */
  reward: number;
  /** Streak day of the latest claim (1-7). */
  streak: number;
  /** Epoch seconds of the latest claim. */
  claimedAt: number;
  wallet: Wallet;
  /** Phase 3: side lights the claim lit on the last road slot played
   *  (`road_story`); absent otherwise. */
  readyLights?: MorningLights;
}

/** {@code POST /v1/players/{id}/wallet/daily-quest}. {@code claimed} is
 *  false when the player already claimed in the last 24 hours: nothing was
 *  credited, and {@code claimedAt} is that claim. */
export interface DailyQuestResponse {
  claimed: boolean;
  /** Coins credited by this claim; 0 when {@code claimed} is false. */
  coins: number;
  /** Gems credited by this claim; 0 when {@code claimed} is false. */
  gems: number;
  /** Epoch seconds of the latest claim. */
  claimedAt: number;
  wallet: Wallet;
}

/** A paid spin's bet crossed one or more levels. The gems are already in
 *  the response's wallet. */
export interface LevelUp {
  /** Level reached. */
  level: number;
  /** Gems credited for the levels crossed. */
  gems: number;
}

export interface GameSession {
  id: SessionId;
  playerId: PlayerId;
  gameRoomId: GameRoomId;
  score: number;
  started: string;
  ended: string | null;
}

export interface Score {
  id: ScoreId;
  playerId: PlayerId;
  gameSessionId: SessionId;
  gameType: GameType;
  value: number;
  achievedAt: string;
}

export interface EndGameSessionResponse {
  session: GameSession;
  score: Score;
  wallet: Wallet;
  coinsAwarded: number;
}

export interface StartGameSessionRequest {
  playerId: PlayerId;
  gameRoomId: GameRoomId;
}

export interface EndGameSessionRequest {
  score: number;
}

// ---- Slot spin (Phase D.1) ----
//
// The server is authoritative for slot outcomes from this point. The
// client kicks off `POST /v1/sessions/{id}/spin` with a bet; the
// response carries the outcome grid (symbol ids), settlement, the
// audit-row id, and the post-spin wallet so the HUD updates without a
// follow-up fetch.

export interface SpinSlotRequest {
  /** Bet in coins. Server pessimistically locks the wallet, validates
   * affordability, debits, runs the spin, settles, credits — atomically
   * inside a single transaction. */
  bet: number;
  /** Theme of the slot being played. Optional; moves the Sweet Trail on
   *  slots that have one (same maths either way). */
  themeId?: string;
}

/** {@code POST /v1/sessions/{id}/gamble}: double-or-nothing on the session's
 *  latest win. {@code step} is 1 for the first gamble on that win. */
export interface GambleSlotRequest {
  spinId: string;
  step: number;
  guess: 'red' | 'black';
  /** The slot being played; the server refuses a gamble on a Royal Road slot. */
  themeId?: string;
}

/** The server's answer to one gamble step. {@code coins} is the wallet after
 *  it: the wallet before the spin's win plus {@code potAfter}. */
export interface GambleSlotResponse {
  spinId: string;
  step: number;
  guess: 'red' | 'black';
  colour: 'red' | 'black';
  won: boolean;
  potBefore: number;
  potAfter: number;
  coins: number;
}

/** One Sweet Trail square, or one hidden bonus prize. */
export interface TrailSquare {
  kind: 'START' | 'COINS' | 'GEMS' | 'FREE_SPINS' | 'BONUS';
  amount: number;
}

/** A hop made on a spin: the square landed on and what it paid. */
export interface TrailStep {
  position: number;
  kind: TrailSquare['kind'];
  amount: number;
}

export interface TrailPick {
  index: number;
  kind: TrailSquare['kind'];
  amount: number;
}

/** Pick-a-prize at the end of the trail. {@code prizes} (every box) is
 *  only sent once {@code finished}. {@code game} is the mini-game the bonus
 *  rolled instead (trail_games), opened with {@code POST .../trail/game};
 *  null or absent for the boxes (a server without trail games never sends
 *  it). */
export interface TrailBonus {
  boxes: number;
  picksLeft: number;
  picks: TrailPick[];
  finished: boolean;
  prizes: TrailSquare[] | null;
  game?: PlayKind | null;
}

/** The trail's mini-game while it's offered or open: enough to fetch the
 *  play and open it again. */
export interface TrailGame {
  playId: string;
  kind: PlayKind;
}

/**
 * Sweet Trail: a per-slot board game beside the reels. Collector symbols
 * fill {@code meter}; every {@code stepCost} moves the token one square
 * along {@code squares} and pays it; the last square opens a pick-a-prize
 * bonus (or a mini-game, with trail_games), then the trail restarts one
 * level up. Server-authoritative.
 * {@code collected} / {@code steps} describe the spin that produced it.
 */
export interface SlotTrailState {
  themeId: string;
  collectorSymbolId: string;
  level: number;
  position: number;
  meter: number;
  stepCost: number;
  squares: TrailSquare[];
  collected: number;
  steps: TrailStep[];
  bonus: TrailBonus | null;
  /** The trail's game while it's offered or open (left by a reload, say):
   *  only on {@code GET .../trail} and the open answer, never on a spin. */
  game?: TrailGame | null;
}

/** {@code POST /v1/sessions/{id}/trail/pick} response. */
export interface TrailPickResponse {
  trail: SlotTrailState;
  wallet: Wallet;
}

/** {@code POST /v1/sessions/{id}/trail/game} response: the trail one level
 *  up (with {@code game} set), the play to open, the gems it paid on
 *  opening and the wallet they're already in. */
export interface TrailGameResponse {
  trail: SlotTrailState;
  offer: PlayOffer;
  gems: number;
  wallet: Wallet;
}

export interface WindowPosition {
  col: number;
  row: number;
}

export interface SlotLineSettlement {
  lineId: string;
  matchSymbolId: string;
  matchCount: number;
  hadWild: boolean;
  winnings: number;
}

export interface SlotScatterSettlement {
  scatterCount: number;
  matchSymbolId: string;
  winnings: number;
  positions: WindowPosition[];
}

export interface SlotSpinSettlement {
  totalWinnings: number;
  bet: number;
  /** {@code totalWinnings - bet}. Negative = net loss for the spin. */
  net: number;
  settlements: SlotLineSettlement[];
  scatter: SlotScatterSettlement | null;
}

export interface ServerSpinOutcome {
  /** Outer = reels (left-to-right), inner = rows (top-to-bottom).
   * Each entry is a stable symbol id (e.g. "cherry", "star"). */
  grid: string[][];
}

/** One gem cell a spin landed (`reel_gems`): where, which cell, and the gems
 *  it paid. `col` runs 0 to 4 from the left and `row` 0 to 2 from the top,
 *  as in `outcome.grid`. */
export interface ReelGemCell {
  col: number;
  row: number;
  /** `gem_t5` or `gem_t10`. */
  symbolId: string;
  gems: number;
}

/** The gem cells a spin landed on the reels (`reel_gems`) and the gems they
 *  paid in all, already in the spin's `wallet`. */
export interface ReelGems {
  gems: number;
  cells: ReelGemCell[];
}

export interface SpinSlotResponse {
  /** {@code slot_spins.id} — audit row primary key. */
  spinId: string;
  outcome: ServerSpinOutcome;
  settlement: SlotSpinSettlement;
  /** Wallet after debit + credit. */
  wallet: Wallet;
  /** Phase D.2 — true iff this spin consumed one of the player's
   * banked free spins (no wallet debit, bet forced to trigger-time bet). */
  wasFreeSpin: boolean;
  /** Phase D.2 — free spins granted by this spin's scatter (0 when no
   * trigger). Already added to the counter; client renders fanfare. */
  freeSpinsGranted: number;
  /** Phase D.2 — free spins banked AFTER this spin. Client mirrors
   * this into its local model.freeSpinsRemaining. */
  freeSpinsRemaining: number;
  /** Phase D.3 — hold-and-spin bonus state when active or just
   * completed. Null on regular base spins with no trigger. */
  holdAndSpin: HoldAndSpinResponseState | null;
  /** Sweet Trail after this spin; absent / null on slots without one. */
  trail?: SlotTrailState | null;
  /** Set when this spin's bet crossed a level; null otherwise. */
  levelUp?: LevelUp | null;
  /** Royal Road after this spin, for flagged players on a road slot;
   *  absent for everyone else. */
  road?: RoadState;
  /** The Lamp Run this spin started; absent otherwise. */
  boardRun?: BoardRunState;
  /** Phase 3: what the showrunner made happen on this spin; absent otherwise. */
  beat?: Beat;
  /** Coins a free refill added before this spin, because the wallet
   *  couldn't cover the bet; absent otherwise. Already in `wallet`. */
  refill?: number;
  /** Reel collectibles (`reel_collect`): the slot's relics after this spin;
   *  absent without the flag. A completed set's prize is already in `wallet`. */
  collect?: CollectState;
  /** Gem cells on the reels (`reel_gems`): what this spin's gem cells paid,
   *  paid or free spin. Absent when none landed, and always without the
   *  flag. Already in `wallet.gems`, so the app shows them and counts them
   *  once, never from the cells' own values. */
  reelGems?: ReelGems;
}

export interface ServerLockedMoneyCell {
  col: number;
  row: number;
  value: number;
  /** Bonus round in which this cell landed. 0 = trigger spin,
   * 1 = first respin, etc. */
  roundLanded: number;
}

export interface HoldAndSpinResponseState {
  /** "started" on the trigger spin · "active" on each respin
   * · "completed" on the spin that ended the bonus. */
  phase: 'started' | 'active' | 'completed';
  lockedCells: ServerLockedMoneyCell[];
  respinsRemaining: number;
  round: number;
  /** Trigger-time bet, locked through the run. */
  bet: number;
  /** Sum of locked cell values. Authoritative on {@code "completed"}
   * — already credited to the wallet by then. */
  totalBonusWinnings: number;
  /** True only on {@code "completed"} when every grid cell was
   * filled — GRAND fanfare trigger. */
  fullGrid: boolean;
}

// ---- World map (W7 plan) ----
//
// Vertical-scrolling Candy-Crush-style progression map. Five biomes
// stacked top-to-bottom; ~8 nodes per biome on a snaking path; player
// taps a node to open a preview, hits Play to launch that game.
//
// Bundled here so server can deliver biome + node config without an
// app update — episodes ship as data, not code.

/**
 * Single source of truth for themed slot variants. Each entry defines a
 * slot node type that ships its own reel art + win-anim webms under
 * {@code apps/mobile/src/assets/reel/<theme>/} and a chunky-coin badge
 * under {@code apps/mobile/src/assets/map_node/<nodeType>.png}.
 *
 * <p>Everything else about a themed slot is DERIVED from this table — add
 * one entry here (plus a {@link TYPES_BY_BIOME} / {@link NAMES_BY_BIOME}
 * position) and the following all update automatically:
 * <ul>
 *   <li>the {@link WorldNodeType} union members ({@link ThemedSlotType})
 *   <li>{@link WORLD_NODE_TYPES} meta (label / icon / color)
 *   <li>{@link SLOT_NODE_TYPES} membership (gem economy)
 *   <li>{@code themeIdForNode} mapping (apps/mobile themes/slotTheme.ts)
 *   <li>{@code nodeTypeToScene} routing (WorldMapView.tsx) — all → 'slot'
 *   <li>the map-node badge {@code ICON_BY_TYPE} records (MapNode /
 *       MapNodePlatform — globs are wildcarded to {@code *_reels.png})
 * </ul>
 *
 * <p>The {@code scaffoldSlot} generator writes this entry automatically
 * from a slot spec; hand-editing is only for one-offs.
 */
export interface ThemedSlotDef {
  /** Reel-folder / theme-registry id, e.g. {@code 'royal'}. Conventionally
   *  the node type minus the {@code _reels} suffix, but kept explicit so a
   *  future slot can diverge from that convention. */
  theme: string;
  /** Display label — mirrored into {@link WorldNodeTypeMeta.label}. */
  label: string;
  /** Unicode glyph fallback for the v0 map (before the badge loads). */
  icon: string;
  /** Hex coin-shell color — matches the AssetRegistry badge palette so the
   *  v0 fallback coin and the production badge agree. */
  color: string;
}

export const THEMED_SLOTS = {
  // <scaffold:themed-slots> — scaffoldSlot prepends new slot entries here
  sugarplum_vault_reels: { theme: 'sugarplum_vault', label: 'Sugarplum Vault', icon: '👑', color: '#8E3BD6' },
  peppermint_pipeworks_reels: { theme: 'peppermint_pipeworks', label: 'Peppermint Pipeworks', icon: '🍬', color: '#FF4D4D' },
  fizz_factory_reels: { theme: 'fizz_factory', label: 'Fizz Factory', icon: '⚙️', color: '#9B4DFF' },
  gummy_lab_reels: { theme: 'gummy_lab', label: 'Gummy Lab', icon: '🧪', color: '#2EC5B6' },
  cocoa_river_reels: { theme: 'cocoa_river', label: 'Cocoa River', icon: '🍫', color: '#8A4A26' },
  lollipop_lane_reels: { theme: 'lollipop_lane', label: 'Lollipop Lane', icon: '🍭', color: '#FF3D9A' },
  sherbet_square_reels: { theme: 'sherbet_square', label: 'Sherbet Square', icon: '🍧', color: '#FF7A3C' },
  glitch_reels: { theme: 'glitch', label: 'Glitch', icon: '👾', color: '#FF2EA8' },
  skyline_reels: { theme: 'skyline', label: 'Skyline', icon: '🌆', color: '#C89030' },
  speedway_reels: { theme: 'speedway', label: 'Speedway', icon: '🏎️', color: '#2090E0' },
  cyber_heist_reels: { theme: 'cyber_heist', label: 'Cyber Heist', icon: '💾', color: '#8A40C0' },
  synthwave_reels: { theme: 'synthwave', label: 'Synthwave', icon: '🌅', color: '#B040D0' },
  arcade_reels: { theme: 'arcade', label: 'Arcade', icon: '🕹️', color: '#E0457B' },
  high_noon_reels: { theme: 'high_noon', label: 'High Noon', icon: '🌵', color: '#C25A3F' },
  stampede_reels: { theme: 'stampede', label: 'Stampede', icon: '🐃', color: '#9A6B3F' },
  iron_horse_reels: { theme: 'iron_horse', label: 'Iron Horse', icon: '🚂', color: '#5E6670' },
  bounty_reels: { theme: 'bounty', label: 'Bounty', icon: '⭐', color: '#B0833A' },
  saloon_reels: { theme: 'saloon', label: 'Saloon', icon: '🍺', color: '#B5532E' },
  gold_mine_reels: { theme: 'gold_mine', label: 'Gold Mine', icon: '⛏️', color: '#C68A3A' },
  glacier_keep_reels: { theme: 'glacier_keep', label: 'Glacier Keep', icon: '🏰', color: '#3E78B0' },
  hot_springs_reels: { theme: 'hot_springs', label: 'Hot Springs', icon: '♨️', color: '#5FB8B0' },
  yeti_reels: { theme: 'yeti', label: 'Yeti\'s Den', icon: '👣', color: '#88B0C8' },
  aurora_reels: { theme: 'aurora', label: 'Aurora', icon: '🌌', color: '#8E6FD0' },
  ice_cavern_reels: { theme: 'ice_cavern', label: 'Ice Cavern', icon: '🧊', color: '#4FB0C8' },
  avalanche_reels: { theme: 'avalanche', label: 'Avalanche', icon: '🏔️', color: '#6FA8DC' },
  buried_treasure_reels: { theme: 'buried_treasure', label: 'Buried Treasure', icon: '💰', color: '#E8B53A' },
  davy_jones_reels: { theme: 'davy_jones', label: 'Davy Jones', icon: '💀', color: '#3FB58C' },
  stormy_seas_reels: { theme: 'stormy_seas', label: 'Pirate Plunder', icon: '☠️', color: '#5E7C99' },
  mermaid_cove_reels: { theme: 'mermaid_cove', label: 'Mermaid Cove', icon: '🧜‍♀️', color: '#5BC6C6' },
  map_fragment_reels: { theme: 'map_fragment', label: 'Map Fragment', icon: '🗺️', color: '#C9A24B' },
  kraken_reels: { theme: 'kraken', label: 'Kraken\'s Lair', icon: '🐙', color: '#2FA4B8' },
  royal_reels: { theme: 'royal', label: 'Royal Reels', icon: '👑', color: '#C73E3E' },
  dragon_reels: { theme: 'dragon', label: "Dragon's Hoard", icon: '🐉', color: '#FF8C42' },
  knights_reels: { theme: 'knights', label: "Knights' Quest", icon: '⚔️', color: '#3E5BAF' },
  wizard_reels: { theme: 'wizard', label: "Wizard's Tower", icon: '🔮', color: '#5E3A8C' },
  // Polite interlude in the Kingdom arc — between Royal Court and Knights'
  // Quest. Rose-pink badge fills the palette gap (red / orange / blue /
  // purple) left by the other four.
  gardens_reels: { theme: 'gardens', label: 'Castle Gardens', icon: '🌹', color: '#E48FA0' },
  // Captured beat — between Wizard's Tower and Dragon's Hoard. Torchlight-
  // amber against dungeon dark; high-variance "key collector" identity.
  dungeons_reels: { theme: 'dungeons', label: 'Castle Dungeons', icon: '🗝️', color: '#E87B3A' },
  // Egypt (Sunscar Sands) — Episode 6. Desert Caravan is the free starter
  // (first slot per biome is free by FREE_SLOT_NODE_IDS); the rest run the
  // tomb → riches → queen → spell → underworld → sun-god arc. Badges span
  // sand-amber / sandstone / pharaoh-gold / lapis / scarab-green /
  // underworld-indigo / radiant-sun so the seven read distinctly.
  caravan_reels: { theme: 'caravan', label: 'Desert Caravan', icon: '🐫', color: '#D9A441' },
  pharaoh_tomb_reels: { theme: 'pharaoh_tomb', label: "Pharaoh's Tomb", icon: '🏺', color: '#C2945A' },
  pharaohs_riches_reels: { theme: 'pharaohs_riches', label: "Pharaoh's Riches", icon: '👑', color: '#E0A52E' },
  cleopatra_reels: { theme: 'cleopatra', label: "Cleopatra's Palace", icon: '🐍', color: '#2E78B0' },
  book_of_dead_reels: { theme: 'book_of_dead', label: 'Book of the Dead', icon: '📖', color: '#3E9E72' },
  anubis_reels: { theme: 'anubis', label: 'Anubis Underworld', icon: '⚖️', color: '#6A5AC0' },
  eye_of_ra_reels: { theme: 'eye_of_ra', label: 'Eye of Ra', icon: '☀️', color: '#F2C23A' },
} as const satisfies Record<string, ThemedSlotDef>;

/** Node-type ids of every themed slot — derived from {@link THEMED_SLOTS}. */
export type ThemedSlotType = keyof typeof THEMED_SLOTS;

export type WorldNodeType =
  | 'slot'
  | 'bingo'
  | 'card'
  | 'scratch'
  | 'mini'
  | 'boss'
  | 'chest'
  | 'daily'
  | 'wheel'
  | 'locked'
  // Kingdom-biome themed slot variants — see THEMED_SLOTS above.
  | ThemedSlotType;

export interface WorldNodeTypeMeta {
  label: string;
  /** Unicode glyph fallback. Production replaces with custom-illustrated
   * icon assets. */
  icon: string;
  /** Hex color the candy-coin shell renders in. */
  color: string;
}

/** Meta for the non-themed (fixed) node types. Themed-slot meta is
 *  derived from {@link THEMED_SLOTS} and merged in below. */
const BASE_NODE_TYPES: Record<string, WorldNodeTypeMeta> = {
  slot: { label: 'Slots', icon: '🎰', color: '#FFB347' },
  bingo: { label: 'Bingo', icon: '🎱', color: '#FF6B9D' },
  card: { label: 'Cards', icon: '🃏', color: '#7BB3FF' },
  scratch: { label: 'Scratch', icon: '🎟️', color: '#FFD93D' },
  mini: { label: 'Mini-game', icon: '🎲', color: '#9B7BFF' },
  boss: { label: 'Tournament', icon: '👑', color: '#E8344E' },
  chest: { label: 'Chest', icon: '🎁', color: '#FFC857' },
  daily: { label: 'Daily Quest', icon: '⭐', color: '#56D9C5' },
  // Wheel of Fortune — the multiplayer marquee game (Wheel GDD §1).
  // Theme-prefixed name: "Forest Wheel · Whispering Woods" etc.
  wheel: { label: 'Wheel of Fortune', icon: '🎡', color: '#FFC940' },
  locked: { label: 'Coming Soon', icon: '🔒', color: '#8B8E99' },
};

/** Node-type → display meta. Base types are fixed; themed-slot entries
 *  (label / icon / color) are derived from {@link THEMED_SLOTS} so a new
 *  themed slot needs no edit here — its meta comes from its registry row. */
export const WORLD_NODE_TYPES: Record<WorldNodeType, WorldNodeTypeMeta> = {
  ...BASE_NODE_TYPES,
  ...Object.fromEntries(
    Object.entries(THEMED_SLOTS).map(([type, def]) => [
      type,
      { label: def.label, icon: def.icon, color: def.color },
    ]),
  ),
} as Record<WorldNodeType, WorldNodeTypeMeta>;

export interface Biome {
  id: 'kingdom' | 'cove' | 'frost' | 'frontier' | 'neon' | 'egypt' | 'candy';
  name: string;
  /** Episode 1, Episode 2, … */
  chapter: string;
  /** Subtitle on the episode banner. */
  blurb: string;
  /** 3-stop vertical sky gradient: top → mid → bottom. */
  sky: readonly [string, string, string];
  /** Trim color on the episode banner; matches the biome's "voice". */
  accent: string;
  /** Motif kinds used to flavor the biome panel. v0 ports may render
   * solid color blocks; full motif art lands in a later pass. */
  motifs: readonly string[];
}

/** Biome id values, in display order — re-exported as a tuple so the
 *  artefact catalog + any per-biome iteration stays in lockstep with
 *  {@link Biome#id}. */
export type BiomeId = Biome['id'];

/**
 * Retention collectibles (2026-06-06) — one rare "limited edition"
 * artefact hidden in the reels of each biome. Pure prestige: uncovering
 * one adds it to the player's permanent collection (no currency reward).
 * The set of five IS the reward — a reason to keep spinning across every
 * biome rather than churning after the first.
 *
 * <p>{@link ArtefactDef#id} doubles as the biome id, so "which artefact
 * does this biome drop" is identity, not a lookup table. The reel symbol
 * that carries it uses {@code artefact_<id>} as its symbol id and sets
 * {@code SlotSymbolDef.artefactId} to this {@code id}. Settlement keys
 * off that field, so the symbol id naming is cosmetic.
 *
 * <p>Single source of truth: the engine reads {@link ARTEFACTS}.length
 * for the collection total, {@code apps/mobile/src/themes} injects the
 * matching artefact into every theme in a biome, and the collection
 * sheet renders one tile per entry.
 */
export interface ArtefactDef {
  /** Biome id — also the artefact id used in events + persistence. */
  id: BiomeId;
  /** Player-facing name shown in the collection + uncover celebration. */
  name: string;
  /** One-line flavour for the collection tile. */
  blurb: string;
  /** Emoji glyph fallback when the commissioned PNG hasn't loaded
   *  (engine-only test scenes, pre-art builds). */
  glyph: string;
}

/** The five biome artefacts, in biome order. Names lean "legendary
 *  relic" so the set reads as a museum collection, distinct from the
 *  regular paytable symbols of each theme. */
export const ARTEFACTS: readonly ArtefactDef[] = [
  { id: 'kingdom',  name: 'Excalibur',        blurb: 'The sword of the rightful ruler.', glyph: '⚔️' },
  { id: 'cove',     name: 'Trident of the Deep', blurb: 'Forged in the drowned palace.',  glyph: '🔱' },
  { id: 'frost',    name: 'The Frozen Heart',  blurb: 'A glacier’s core, never melting.',  glyph: '💠' },
  { id: 'frontier', name: 'The Mother Lode',   blurb: 'The nugget that started the rush.', glyph: '🪙' },
  { id: 'neon',     name: 'The Source Core',   blurb: 'The city’s beating data-heart.',    glyph: '🔷' },
  // <scaffold:artefacts> — scaffoldBiome appends a new biome's artefact above this line
  { id: 'candy', name: 'The Golden Candy Key', blurb: 'The key to every sweet shop in the land, its bow a heart of candy jewels.', glyph: '🗝️' },
  { id: 'egypt', name: 'The Pharaoh’s Crook', blurb: 'The crook and flail of the first pharaoh, banded in gold and lapis.', glyph: '☀️' },
];

/** O(1) artefact lookup by biome id. */
export const ARTEFACT_BY_BIOME: Readonly<Record<BiomeId, ArtefactDef>> =
  Object.fromEntries(ARTEFACTS.map((a) => [a.id, a])) as Record<BiomeId, ArtefactDef>;

/** Total collectable artefacts — the denominator in "N / 5 collected". */
export const ARTEFACT_TOTAL: number = ARTEFACTS.length;

// ---- Special items — tiered collectibles (M2, progression spine) ----
//
// Each biome has THREE special items forming a rarity ladder. The
// LEGENDARY of each biome is its existing ARTEFACT (id == biome id), so the
// museum hero carries through unchanged; the common + rare items are new,
// lower tiers that give rarity something to vary. Since the old app-side
// finds were retired (October 2026) these are the relics' catalogue: a
// slot's world has these three as its relics (engine `relicsFor`), and the
// server decides every find. The common/rare names + glyphs below are
// engineering scaffolding; final naming + art is Jon's to commission.

export type Rarity = 'common' | 'rare' | 'legendary';

/** Per-rarity UI metadata. {@code order} sorts a world's relics (common,
 *  rare, legendary); {@code accent}/{@code glow} colour the find board +
 *  frame; {@code weight} and {@code shakePx} belonged to the retired
 *  app-side finds (the drop roll and its reveal's shake). */
export const RARITY_META: Record<
  Rarity,
  { label: string; order: number; weight: number; accent: string; glow: string; shakePx: number }
> = {
  common:    { label: 'Common',    order: 0, weight: 70, accent: '#9fb2c4', glow: 'rgba(159,178,196,0.55)', shakePx: 4 },
  rare:      { label: 'Rare',      order: 1, weight: 25, accent: '#5aa9ff', glow: 'rgba(90,169,255,0.60)',  shakePx: 9 },
  legendary: { label: 'Legendary', order: 2, weight: 5,  accent: '#ffd24a', glow: 'rgba(255,210,74,0.70)',  shakePx: 16 },
};

export interface SpecialItemDef {
  /** Stable id. Legendary item id == biome id (unifying it with the
   *  existing artefact). Common/rare use {@code `${biome}_c`} / `_r`. */
  id: string;
  name: string;
  blurb: string;
  glyph: string;
  rarity: Rarity;
  biome: BiomeId;
}

/** Common + rare items per biome (the legendary is the biome's ARTEFACT,
 *  appended programmatically in {@link SPECIAL_ITEMS}). Placeholder
 *  content — names/glyphs are scaffolding pending commissioned art. */
const SPECIAL_ITEM_MINORS: Readonly<
  Record<BiomeId, readonly Omit<SpecialItemDef, 'biome'>[]>
> = {
  kingdom: [
    { id: 'kingdom_c', rarity: 'common', name: 'Oaken Shield', glyph: '🛡️', blurb: 'A footsoldier’s dependable guard.' },
    { id: 'kingdom_r', rarity: 'rare', name: 'Golden Goblet', glyph: '🏆', blurb: 'Raised at every royal feast.' },
  ],
  cove: [
    { id: 'cove_c', rarity: 'common', name: 'Sunken Bell', glyph: '🔔', blurb: 'It still rings when the tide turns.' },
    { id: 'cove_r', rarity: 'rare', name: 'Mermaid’s Mirror', glyph: '🪞', blurb: 'It shows the sea as it was.' },
  ],
  frost: [
    { id: 'frost_c', rarity: 'common', name: 'Silver Skate', glyph: '⛸️', blurb: 'Fast on the frozen lake.' },
    { id: 'frost_r', rarity: 'rare', name: 'Snow Globe', glyph: '🏰', blurb: 'An ice castle where it always snows.' },
  ],
  frontier: [
    { id: 'frontier_c', rarity: 'common', name: 'Pocket Watch', glyph: '⏱️', blurb: 'Still keeping frontier time.' },
    { id: 'frontier_r', rarity: 'rare', name: 'Silver Buckle', glyph: '🐂', blurb: 'Won at the biggest rodeo in the west.' },
  ],
  neon: [
    { id: 'neon_c', rarity: 'common', name: 'Neon Headphones', glyph: '🎧', blurb: 'The city’s beat, always on.' },
    { id: 'neon_r', rarity: 'rare', name: 'Chrome Keytar', glyph: '🎹', blurb: 'Played on the brightest rooftop.' },
  ],
  egypt: [
    { id: 'egypt_c', rarity: 'common', name: 'Golden Sandals', glyph: '👡', blurb: 'They walked the length of the Nile.' },
    { id: 'egypt_r', rarity: 'rare', name: 'Sphinx Statuette', glyph: '🦁', blurb: 'It keeps a riddle no one has solved.' },
  ],
  candy: [
    { id: 'candy_c', rarity: 'common', name: 'Frosted Cupcake', glyph: '🧁', blurb: 'Baked for the first candy fair.' },
    { id: 'candy_r', rarity: 'rare', name: 'Gingerbread House', glyph: '🏠', blurb: 'Iced by the sugar elves.' },
  ],
};

/** The full 3-per-biome ladder: each biome's two minors + its ARTEFACT as
 *  the legendary. 7 biomes x 3 = 21 items. */
export const SPECIAL_ITEMS: readonly SpecialItemDef[] = ARTEFACTS.flatMap((a) => {
  const legendary: SpecialItemDef = {
    id: a.id, name: a.name, blurb: a.blurb, glyph: a.glyph, rarity: 'legendary', biome: a.id,
  };
  const minors = (SPECIAL_ITEM_MINORS[a.id] ?? []).map((m) => ({ ...m, biome: a.id }));
  return [...minors, legendary];
});

/** O(1) special-item lookup by id. */
export const SPECIAL_ITEM_BY_ID: Readonly<Record<string, SpecialItemDef>> =
  Object.fromEntries(SPECIAL_ITEMS.map((s) => [s.id, s])) as Record<string, SpecialItemDef>;

/** Total collectable special items across all biomes. */
export const SPECIAL_ITEM_TOTAL: number = SPECIAL_ITEMS.length;

/** A biome's three items, in rarity order (common → rare → legendary). */
export function specialItemsForBiome(biome: BiomeId): readonly SpecialItemDef[] {
  return SPECIAL_ITEMS.filter((s) => s.biome === biome).sort(
    (x, y) => RARITY_META[x.rarity].order - RARITY_META[y.rarity].order,
  );
}

/** Map a world-node id to its biome id, or {@code null} for unknown nodes. */
export function biomeIdForNodeId(nodeId: number): BiomeId | null {
  const node = WORLD_NODES.find((n) => n.id === nodeId);
  if (!node) return null;
  return BIOMES[node.biome]?.id ?? null;
}

/**
 * The single biome-agnostic symbol id the SERVER puts in its shared
 * slot pool for artefacts. The server has one pool for every theme and
 * no notion of which biome is being rendered, so it can't drop a
 * biome-specific artefact id. Instead it drops this generic marker;
 * the client's {@code toEngineOutcome} remaps it to the active biome's
 * artefact def at render time. Keeping the constant in shared means the
 * Java {@code SlotConfigCatalog} and the TS reshape can't drift on the
 * spelling. (Java mirrors the literal — see SlotConfigCatalog.) */
export const GENERIC_ARTEFACT_SYMBOL_ID = 'artefact';

export interface WorldMapNode {
  /** Stable 1-indexed id, unique across the whole map. */
  id: number;
  type: WorldNodeType;
  /** Player-facing display name ("Royal Reels", "Pirate Plunder"). */
  name: string;
  /** Fractional x position within the biome's panel width (0..1). */
  x: number;
  /** Fractional y position within the biome's vertical band (0..1). */
  y: number;
  /** Index of the biome this node belongs to (0..BIOMES.length-1). */
  biome: number;
}

/** Pixel height per biome panel — tuned for visible variety on phone widths. */
export const BIOME_HEIGHT_PX = 900;
/** Panel width — iPhone Pro portrait safe area. */
export const WORLD_PANEL_WIDTH_PX = 402;

/** Snake X-pattern — fractional of panel width. Canonical 8-node
 *  snake used by the four non-Kingdom biomes. Kingdom now hosts 9
 *  nodes (the themed-slot narrative arc; Castle Dungeons added as
 *  the captured beat between Wizard and Dragon), so we extend the
 *  snake by one entry to cover that 9th position. Future themed-
 *  slot additions (Ogre's Bridge, etc.) extend further. */
const SNAKE_X = [0.28, 0.55, 0.78, 0.62, 0.3, 0.48, 0.72, 0.5, 0.28] as const;

export const BIOMES: readonly Biome[] = [
  {
    id: 'kingdom',
    name: 'Greendale Kingdom',
    chapter: 'Episode 1',
    blurb: 'The journey begins',
    sky: ['#A8DCF3', '#CFEBC7', '#7FB069'],
    accent: '#3E7A3E',
    motifs: [
      'castle',
      'tree',
      'cottage',
      'ogre_cave',
      'tree',
      'wildflowers',
      'windmill',
      'flag',
    ],
  },
  {
    id: 'cove',
    name: 'Saltspray Cove',
    chapter: 'Episode 2',
    blurb: 'X marks every spot',
    sky: ['#FFD89B', '#FF9B7A', '#3D8BC4'],
    accent: '#BF4F2A',
    motifs: [
      'lighthouse',
      'palm',
      'ship',
      'island',
      'message_in_bottle',
      'wave',
      'crab',
      'pirate_flag',
    ],
  },
  {
    id: 'frost',
    name: 'Frostpeak Range',
    chapter: 'Episode 3',
    blurb: 'Climb to glory',
    sky: ['#C5D9F1', '#E8F1FB', '#FFFFFF'],
    accent: '#4A6FA5',
    motifs: [
      'peak',
      'pine',
      'igloo',
      'iceberg_penguin',
      'aurora',
      'snowman',
      'ice_castle',
      'frost_crystal',
    ],
  },
  {
    id: 'frontier',
    name: 'Dustwood Frontier',
    chapter: 'Episode 4',
    blurb: 'High noon, high stakes',
    sky: ['#F4B860', '#E78E5C', '#C25A3F'],
    accent: '#7A2E1F',
    motifs: [
      'mesa',
      'saloon',
      'cactus',
      'wagon',
      'windmill_west',
      'cattle_skull',
      'tumbleweed',
      'gold_pan',
    ],
  },
  {
    id: 'neon',
    name: 'Neon Heights',
    chapter: 'Episode 5',
    blurb: 'Lights, gems, jackpot',
    sky: ['#1A0B3D', '#4A1A6B', '#FF2EA8'],
    accent: '#FFD700',
    motifs: [
      'skyline',
      'tower',
      'marquee',
      'sign',
      'hovercar',
      'monorail',
      'neon_palm',
      'arcade',
    ],
  },
  {
    id: 'egypt',
    name: 'Sunscar Sands',
    chapter: 'Episode 6',
    blurb: 'Where kings sleep in gold',
    // Desert sunset: sun-gold → amber → terracotta dusk.
    sky: ['#FCE3A0', '#E8B15E', '#B86A2E'],
    accent: '#C9962E',
    motifs: [
      'pyramid',
      'sphinx',
      'obelisk',
      'palm',
      'scarab',
      'sarcophagus',
      'sun_disc',
      'ankh',
    ],
  },
  // <scaffold:biomes> — scaffoldBiome appends a new biome entry above this line (kept last = next episode)
  {
    id: 'candy',
    name: 'Wonderfizz Works',
    chapter: 'Episode 7',
    blurb: 'Pull the lever, taste the impossible',
    sky: ['#FFF4C2', '#FF9FD0', '#88D4F5'],
    accent: '#B04CFF',
    motifs: [
      'lollipop',
      'gumdrop',
      'candy_cane',
      'soda_fountain',
      'cupcake',
      'gummy_bear',
      'gear_cog',
      'peppermint_swirl',
    ],
  },
];

/** Per-biome ordered (length 8) node-type list.
 *
 * <p>One {@code 'wheel'} slot per biome — the multiplayer Wheel-of-Fortune
 * marquee. Indexes were chosen so the wheel sits roughly mid-biome on
 * the snake path (visible without scrolling to the boss). */
const TYPES_BY_BIOME: readonly (readonly WorldNodeType[])[] = [
  // Kingdom: a 7-node slot-only arc. The player "sets off in the
  // forest of animals, meets the royals, the knights joust, the
  // wizard plots, the dungeon captures, the dragon looms" — each
  // themed reel slot is a story beat in that order. Castle Gardens
  // sits between Royal and Knights as a polite interlude (gentlest
  // slot in the deck); Castle Dungeons sits between Wizard and
  // Dragon as the captured beat (highest-variance slot in the
  // deck — "key collector" bonus).
  //
  // Producer brief 2026-06-06: Daily Quest + Hidden Treasure (chest)
  // are pulled from every biome for now — keep the row to slots only
  // so the gem-purchase loop is the headline mechanic without a
  // free-coin drip muddying the message. They can come back when the
  // economy is balanced and the daily / pick-em flows have shipped
  // their own producer reviews.
  [
    'slot',
    'royal_reels',
    'gardens_reels',
    'knights_reels',
    'wizard_reels',
    'dungeons_reels',
    'dragon_reels',
  ],
  // Cove — themed-slot arc: base slot → 6 themed reels.
  ['slot', 'map_fragment_reels', 'mermaid_cove_reels', 'stormy_seas_reels', 'davy_jones_reels', 'kraken_reels', 'buried_treasure_reels'],
  // Frost — themed-slot arc: base slot → 6 themed reels.
  ['slot', 'avalanche_reels', 'ice_cavern_reels', 'aurora_reels', 'yeti_reels', 'hot_springs_reels', 'glacier_keep_reels'],
  // Frontier — themed-slot arc: base slot → 6 themed reels.
  ['slot', 'gold_mine_reels', 'saloon_reels', 'bounty_reels', 'iron_horse_reels', 'stampede_reels', 'high_noon_reels'],
  // Neon — themed-slot arc: base slot → 6 themed reels.
  ['slot', 'arcade_reels', 'synthwave_reels', 'cyber_heist_reels', 'speedway_reels', 'skyline_reels', 'glitch_reels'],
  // Egypt — Desert Caravan (free) → tomb → riches → queen → spell →
  // underworld → sun-god. All 7 are themed reels (uniform scaffold).
  ['caravan_reels', 'pharaoh_tomb_reels', 'pharaohs_riches_reels', 'cleopatra_reels', 'book_of_dead_reels', 'anubis_reels', 'eye_of_ra_reels'],
  // <scaffold:types-by-biome> — scaffoldBiome appends a new biome's node-type row above this line
  ['sherbet_square_reels', 'lollipop_lane_reels', 'cocoa_river_reels', 'gummy_lab_reels', 'fizz_factory_reels', 'peppermint_pipeworks_reels', 'sugarplum_vault_reels'],
];

/** Per-biome ordered (length 8) display names. Wheel rooms use the
 * "{Theme} Wheel · {Sub-name}" pattern (UX Spec §3.1) so the cast can
 * be greeted with the same theme line everywhere it appears.
 *
 * <p>Producer audit Phase 3.4 — the 8th node of each biome is the
 * crash game (boss/climb mechanic). The legacy combat names ("Royal
 * Joust", "Outlaw Duel", "Kraken Battle") didn't communicate the
 * climb-then-crash gameplay, so each biome's boss has been renamed
 * to a climb / rise metaphor that reads as crash:
 * <ul>
 *   <li>kingdom  → Castle Climb (climb the keep, jump before the fall)
 *   <li>cove     → Tide Tower (ride the rising tide)
 *   <li>frost    → Summit Showdown — kept; "summit" already evokes climb
 *   <li>frontier → Pillar Plunge (gold-rush rope-tower)
 *   <li>neon     → Skyline Stack (stack the high-roller multipliers)
 * </ul>
 */
const NAMES_BY_BIOME: readonly (readonly string[])[] = [
  [
    'Forest Reels',
    'Royal Reels',
    'Castle Gardens',
    "Knights' Quest",
    "Wizard's Tower",
    'Castle Dungeons',
    "Dragon's Hoard",
  ],
  ['Stormy Seas', 'Map Fragment', 'Mermaid Cove', 'Pirate Plunder', 'Davy Jones', "Kraken's Lair", 'Buried Treasure'],
  ['Frost Reels', 'Avalanche', 'Ice Cavern', 'Aurora', "Yeti's Den", 'Hot Springs', 'Glacier Keep'],
  ['Dustwood Reels', 'Gold Mine', 'Saloon', 'Bounty', 'Iron Horse', 'Stampede', 'High Noon'],
  ['Neon Reels', 'Arcade', 'Synthwave', 'Cyber Heist', 'Speedway', 'Skyline', 'Glitch'],
  ['Desert Caravan', "Pharaoh's Tomb", "Pharaoh's Riches", "Cleopatra's Palace", 'Book of the Dead', 'Anubis Underworld', 'Eye of Ra'],
  // <scaffold:names-by-biome> — scaffoldBiome appends a new biome's display-name row above this line
  ['Sherbet Square', 'Lollipop Lane', 'Cocoa River', 'Gummy Lab', 'Fizz Factory', 'Peppermint Pipeworks', 'Sugarplum Vault'],
];

/**
 * Theme-registry id → owning biome id, derived from {@link TYPES_BY_BIOME}
 * (parallel to {@link BIOMES}) joined with {@link THEMED_SLOTS} for the
 * authoritative theme id. Covers EVERY themed slot of every biome
 * automatically — including biomes with no commissioned music yet
 * (egypt, candy). The generic {@code 'slot'} biome-hero nodes are absent
 * from THEMED_SLOTS and so are skipped here; their theme is biome-defaulted
 * via {@code themeIdForBiome} (App.tsx). Consumers that need a theme→biome
 * lookup (e.g. the mobile artefact-injection patch loop) should prefer
 * this over re-deriving from the audio table, which only lists themes that
 * ship a music track.
 */
export const BIOME_BY_THEME_ID: Readonly<Record<string, BiomeId>> =
  Object.fromEntries(
    TYPES_BY_BIOME.flatMap((types, b) =>
      types.flatMap((t) => {
        const def = THEMED_SLOTS[t as keyof typeof THEMED_SLOTS];
        return def ? [[def.theme, BIOMES[b]!.id] as [string, BiomeId]] : [];
      }),
    ),
  );

function buildWorldNodes(): readonly WorldMapNode[] {
  const nodes: WorldMapNode[] = [];
  // Cumulative id so biomes can have different node counts (kingdom
  // is now 12 with the themed-slot additions; the other four stay
  // at 8). Previously the formula was {@code b * 8 + i + 1} which
  // assumed every biome had exactly 8 nodes — a stale invariant
  // after spreading the new royal/dragon/knights/wizard reels into
  // the kingdom path.
  let nextId = 1;
  for (let b = 0; b < BIOMES.length; b++) {
    const types = TYPES_BY_BIOME[b]!;
    const names = NAMES_BY_BIOME[b]!;
    for (let i = 0; i < types.length; i++) {
      nodes.push({
        id: nextId++,
        type: types[i]!,
        name: names[i]!,
        x: SNAKE_X[i % SNAKE_X.length]!,
        y: (i + 0.5) / types.length,
        biome: b,
      });
    }
  }
  return nodes;
}

/** All nodes in the world, id 1..N in player-order. Length varies:
 *  Kingdom has 12 (themed-slot additions), the other four biomes have
 *  8 each, total 44 at the time of writing. */
export const WORLD_NODES: readonly WorldMapNode[] = buildWorldNodes();

// ---------------------------------------------------------------------------
// T1 — gem economy / slot unlocks (2026-05-28)
// ---------------------------------------------------------------------------
//
// Producer brief: players spend gems won in matches to unlock new slot
// games. No progression gates — slots can be bought in any order. First
// slot machine in each biome is open by default. Starting grant: 100 gems
// (see {@code AddWalletService.INITIAL_GEMS} server-side).
//
// Pricing model: flat 10 gems per non-free slot. Tuning hooks live here
// (cost per slot type, "is this a slot" filter) so the resolver layer
// stays free of policy. Adjust prices in {@link SLOT_GEM_PRICE} when the
// economy needs retuning; client + server both read this single table.
//
// "First slot per biome free" is DERIVED at read time from
// {@link TYPES_BY_BIOME} rather than seeded as unlock records on
// account creation. New biomes don't need a backfill — they ship with
// their first slot already free, identified by index 0 of their
// {@link TYPES_BY_BIOME} entry that matches a slot type.

/**
 * The set of {@link WorldNodeType} values that count as "slots" for
 * the purposes of the gem-unlock economy. Daily / Chest / Wheel / Boss
 * / Card / Bingo / Scratch nodes are not slots and don't participate
 * in this system. Kingdom's themed-slot variants (royal/gardens/...)
 * all count as slots — each is a distinct game with its own art and
 * paytable, so each unlocks independently.
 */
export const SLOT_NODE_TYPES: ReadonlySet<WorldNodeType> = new Set<WorldNodeType>([
  'slot',
  // Every themed slot variant counts — derived from THEMED_SLOTS so new
  // themed slots join the gem economy automatically.
  ...(Object.keys(THEMED_SLOTS) as ThemedSlotType[]),
]);

/**
 * Cost in gems to unlock a slot. Flat rate across every slot type so
 * the economy is legible at a glance — "10 gems per slot, first in each
 * biome free." Bumped by retuning this constant (no per-type override
 * yet; if one biome's themed slots feel under/over-priced relative to
 * the basic 'slot', split this into a `Record<WorldNodeType, number>`
 * keyed by type and the rest of the file's API stays stable).
 *
 * <p>Economy math at the flat 100-gem price for v1 content (2026-06-06
 * retune — producer brief: "we're currently giving too many away" with
 * level-ups dripping 5 gems each on top of the 100-gem starter grant):
 * <ul>
 *   <li>Kingdom: 7 slots (1 free 'slot', 6 paid themed reels) → 600 gems</li>
 *   <li>Cove / Frost / Frontier: 7 slots each (1 free, 6 paid) → 600 gems each</li>
 *   <li>Neon: 7 slots (1 free, 6 paid) → 600 gems</li>
 *   <li>v1 total: 30 paid × 100 = 3,000 gems for full unlock</li>
 *   <li>100-gem starter grant + ~5 gems per level-up = ~20 levels per
 *       slot. Players see steady progress (a level-up funds half a
 *       slot) without burning the unlock pool in the first session.
 *       Earlier 10-gem price let the starter grant fund 10 slots —
 *       the loop collapsed before the player had to engage with the
 *       game's economy. 100 gems puts the early decision back in
 *       front of them: pick one slot per session, not all of them.</li>
 * </ul>
 */
export const SLOT_UNLOCK_PRICE_GEMS: number = 100;

/** Returns true if the given node type is a slot game for the purposes
 *  of the gem-unlock economy. */
export function isSlotNodeType(type: WorldNodeType): boolean {
  return SLOT_NODE_TYPES.has(type);
}

/**
 * For each biome, the {@link WorldMapNode#id} of the first node whose
 * type is a slot — i.e. the slot that's free by default in that biome.
 * Derived once from {@link TYPES_BY_BIOME} so adding a new biome
 * automatically picks up the right free node.
 *
 * <p>Returns a {@code Set<number>} for O(1) "is this the free one"
 * lookups at the slot-tap call site. If a biome contains no slot at
 * all (e.g. a future biome with only wheel/bingo/chest), nothing is
 * added — that biome simply has no free slot.
 */
export const FREE_SLOT_NODE_IDS: ReadonlySet<number> = (() => {
  const out = new Set<number>();
  for (const node of WORLD_NODES) {
    if (!isSlotNodeType(node.type)) continue;
    // Walk biome `b` looking for the first slot — once we find one for
    // this biome, skip the rest. Cheaper than building a per-biome map
    // since N is small (5 biomes × ~8 nodes = 40 entries) and the loop
    // runs once at module load.
    let alreadyHaveOneInThisBiome = false;
    for (const otherId of out) {
      const other = WORLD_NODES.find((n) => n.id === otherId);
      if (other && other.biome === node.biome) {
        alreadyHaveOneInThisBiome = true;
        break;
      }
    }
    if (alreadyHaveOneInThisBiome) continue;
    out.add(node.id);
  }
  return out;
})();

/** Slot id → cost in gems (0 for the free-by-default node in each
 *  biome; {@link SLOT_UNLOCK_PRICE_GEMS} for the rest). Non-slot nodes
 *  return {@code null} — they can't be unlocked because they don't
 *  participate in the economy. */
export function slotGemPrice(nodeId: number): number | null {
  const node = WORLD_NODES.find((n) => n.id === nodeId);
  if (!node || !isSlotNodeType(node.type)) return null;
  return FREE_SLOT_NODE_IDS.has(nodeId) ? 0 : SLOT_UNLOCK_PRICE_GEMS;
}

/**
 * Returns true if the player has access to the given slot. A slot is
 * unlocked if either (a) it's the free-by-default node in its biome,
 * or (b) its id appears in the player's {@code unlockedSlotIds}.
 *
 * <p>The {@code unlockedSlotIds} value comes from the server's
 * {@code Wallet.unlockedSlotIds} (multiplayer) or the client's
 * {@code PlayerProfile.unlockedSlotIds} (solo). Both store ids as
 * strings to keep the wire format and the Postgres {@code text[]}
 * column aligned; we stringify the numeric id here.
 *
 * <p>Non-slot nodes (daily, chest, wheel, boss, …) always return
 * {@code true} — they're not part of the unlock economy.
 */
export function isSlotUnlocked(
  nodeId: number,
  unlockedSlotIds: ReadonlyArray<string>,
): boolean {
  const node = WORLD_NODES.find((n) => n.id === nodeId);
  if (!node) return false;
  if (!isSlotNodeType(node.type)) return true;
  if (FREE_SLOT_NODE_IDS.has(nodeId)) return true;
  return unlockedSlotIds.includes(String(nodeId));
}

// ---- Slot mastery — per-slot stars + medal (M1, 2026-06-26) ----
//
// Each slot earns up to 3 stars, filled by CUMULATIVE COINS WAGERED on
// that slot (not won — wagered measures devotion and can't be gamed by a
// single lucky hit). Star count maps to a bronze / silver / gold medal
// shown on the slot tile + home screen. Gold is terminal (the map is the
// evergreen progression layer). See docs/design/single-player-progression-gdd.md §4.

/** Cumulative coins wagered to reach ★ / ★★ / ★★★ on a single slot.
 *  Global to start; can scale per-biome later from this one knob. */
export const STAR_THRESHOLDS: readonly number[] = [5_000, 25_000, 100_000];

/** The headline medal a slot shows, derived from its star count. */
export type SlotMedal = 'none' | 'bronze' | 'silver' | 'gold';

/** Stars (0–3) earned for a given cumulative-wagered total. Monotonic:
 *  crossing each {@link STAR_THRESHOLDS} entry lights one more star. */
export function slotStarsForWagered(wagered: number): 0 | 1 | 2 | 3 {
  let stars: 0 | 1 | 2 | 3 = 0;
  for (const threshold of STAR_THRESHOLDS) {
    if (wagered >= threshold) stars = (stars + 1) as 0 | 1 | 2 | 3;
    else break;
  }
  return stars;
}

/** Medal for a star count: 1★ bronze, 2★ silver, 3★ gold, 0★ none. */
export function medalForStars(stars: number): SlotMedal {
  if (stars >= 3) return 'gold';
  if (stars === 2) return 'silver';
  if (stars === 1) return 'bronze';
  return 'none';
}

/** Coins-wagered still needed to reach the next star, or {@code null} when
 *  the slot is already gold (terminal). Drives the "next star at…" preview. */
export function wageredToNextStar(wagered: number): number | null {
  const stars = slotStarsForWagered(wagered);
  if (stars >= STAR_THRESHOLDS.length) return null;
  return Math.max(0, STAR_THRESHOLDS[stars]! - wagered);
}

// SSE event types emitted from GameRoomController.subscribe (GET /v1/rooms/{id}/events).
// Sent as named events; consumers should switch on event name.

export interface RoomWelcomeEvent {
  roomId: string;
  subscribers: number;
}

export interface RoomTickEvent {
  roomId: string;
  epochMs: number;
  subscribers: number;
}

export type RoomEventName = 'welcome' | 'tick';

// ---- Wheel of Fortune — bot archetypes (W7) ----
//
// Eight static cast members per Wheel GDD §9. Display names and slider
// values are the contract between server (BotArchetype enum) and client
// (cast strip / profile card). Server is the source of truth at runtime
// (effective sliders include fair-play guard deltas) but these base
// values are immutable and shared.

export type BotArchetype =
  | 'CALM_CARL'
  | 'BRAVE_BEA'
  | 'QUIET_QUINN'
  | 'FRIENDLY_FOX'
  | 'WILD_WILLA'
  | 'SHIELDED_SAM'
  | 'LUCKY_LOU'
  | 'STOIC_STAN';

export interface BotArchetypeMeta {
  /** Stable identifier — also the server-side enum name. */
  id: BotArchetype;
  /** Player-visible name. Static per archetype, forever (Bot Spec §7.3). */
  displayName: string;
  /** One-line tagline shown on the profile card. */
  tagline: string;
  /** Per-archetype accent colour (Visual Spec §4.1) — used for active-
   * state borders, profile-card tints, fan-greet particle hue. */
  accent: string;
  /** Base slider values (Bot Spec §2.2). Effective sliders at runtime
   * are these + fair-play deltas, clamped [0, 1]. */
  sliders: {
    aggression: number;
    caution: number;
    sociality: number;
    risk: number;
  };
}

export const BOT_ARCHETYPES: Record<BotArchetype, BotArchetypeMeta> = {
  CALM_CARL: {
    id: 'CALM_CARL',
    displayName: 'Calm Carl',
    tagline: 'Steady as she goes.',
    accent: '#C58A5A',
    sliders: { aggression: 0.20, caution: 0.70, sociality: 0.30, risk: 0.30 },
  },
  BRAVE_BEA: {
    id: 'BRAVE_BEA',
    displayName: 'Brave Bea',
    tagline: 'Always plays bold.',
    accent: '#FF5A5F',
    sliders: { aggression: 0.70, caution: 0.20, sociality: 0.50, risk: 0.70 },
  },
  QUIET_QUINN: {
    id: 'QUIET_QUINN',
    displayName: 'Quiet Quinn',
    tagline: 'Watches everything.',
    accent: '#5C6BC0',
    sliders: { aggression: 0.50, caution: 0.50, sociality: 0.10, risk: 0.50 },
  },
  FRIENDLY_FOX: {
    id: 'FRIENDLY_FOX',
    displayName: 'Friendly Fox',
    tagline: 'Just here to have a good time.',
    accent: '#FFA050',
    sliders: { aggression: 0.30, caution: 0.40, sociality: 0.80, risk: 0.40 },
  },
  WILD_WILLA: {
    id: 'WILD_WILLA',
    displayName: 'Wild Willa',
    tagline: 'WHOOO! All in!',
    accent: '#E8344E',
    sliders: { aggression: 0.80, caution: 0.10, sociality: 0.60, risk: 0.90 },
  },
  SHIELDED_SAM: {
    id: 'SHIELDED_SAM',
    displayName: 'Shielded Sam',
    tagline: 'Defended.',
    accent: '#1FC8C8',
    sliders: { aggression: 0.10, caution: 0.90, sociality: 0.30, risk: 0.40 },
  },
  LUCKY_LOU: {
    id: 'LUCKY_LOU',
    displayName: 'Lucky Lou',
    tagline: 'Why not me?',
    accent: '#9DE54E',
    sliders: { aggression: 0.40, caution: 0.40, sociality: 0.70, risk: 0.60 },
  },
  STOIC_STAN: {
    id: 'STOIC_STAN',
    displayName: 'Stoic Stan',
    tagline: 'Acceptable outcome.',
    accent: '#7E8DA8',
    sliders: { aggression: 0.50, caution: 0.50, sociality: 0.20, risk: 0.50 },
  },
};

/** Iteration helper — all 8 archetypes in display order. */
export const BOT_ARCHETYPE_LIST: ReadonlyArray<BotArchetypeMeta> = [
  BOT_ARCHETYPES.CALM_CARL,
  BOT_ARCHETYPES.BRAVE_BEA,
  BOT_ARCHETYPES.FRIENDLY_FOX,
  BOT_ARCHETYPES.LUCKY_LOU,
  BOT_ARCHETYPES.SHIELDED_SAM,
  BOT_ARCHETYPES.QUIET_QUINN,
  BOT_ARCHETYPES.WILD_WILLA,
  BOT_ARCHETYPES.STOIC_STAN,
];

// ---- Wheel of Fortune — observable state (W7 SSE wire) ----
//
// TypeScript mirror of the server's
//   com.pixebyte.goodtime.domain.model.roomengine.wheel.WheelObservableState
// Sent over SSE as the {@code state_init} event payload, with
// {@code state_delta} events carrying RFC-6902 JSON Patch arrays
// against the prior view.
//
// Long-shaped fields (version, seatId, tokens, meter, pool values,
// payouts) come through as JS numbers. They are ALL safe within
// 2^53 for the foreseeable future — meter caps in the millions, seat
// ids are ≤ 6 per room, version increments single digits per match.
// If those bounds ever loosen, consider bigint or string-encoded.

/**
 * Wheel-of-Fortune room phase machine. The placement-driven flow runs
 * IDLE → CHARGING → REEL_RACE → STAKE → WHEEL_REVEAL → WHEEL_RESOLVE →
 * PAYOUT → IDLE.
 *
 * <p>{@code WHEEL} is the legacy single-phase resolution (three sub-
 * spins: FLOOR / PRIZE / JACKPOT). Kept in the wire enum for one
 * release cycle so old persisted state can still be decoded; new
 * matches never enter this state.
 */
export type WheelPhase =
  | 'IDLE'
  | 'CHARGING'
  | 'REEL_RACE'
  | 'STAKE'
  | 'WHEEL_REVEAL'
  | 'WHEEL_RESOLVE'
  | 'WHEEL'
  | 'PAYOUT';

/** Player vs bot vs (transient) empty seat. */
export type WheelSeatKind = 'PLAYER' | 'BOT' | 'EMPTY';

/** Legacy three-sub-spin identifier — only present on matches that
 *  resolved through the retired WHEEL phase. */
export type WheelSubSpinKind = 'FLOOR' | 'PRIZE' | 'JACKPOT';

/** Filter level on the SSE stream. */
export type ObserverRole = 'PLAYER' | 'AUDIENCE' | 'DEBUG';

/** One seat's stake-point allocation for the current match. */
export interface WheelStakeAllocation {
  shield: 0 | 1;
  lance: 0 | 1;
  reach: 0 | 1 | 2 | 3 | 4;
  /** Non-null iff lance === 1. */
  lanceTargetSeatId: number | null;
  committed: boolean;
}

/** Server-projected view of one seat. */
export interface WheelSeatView {
  seatId: number;
  kind: WheelSeatKind;
  /** UUID string for PLAYER seats; null for BOT/EMPTY. */
  playerId: string | null;
  /** {@link BotArchetype} for BOT seats; null for PLAYER/EMPTY. */
  archetype: BotArchetype | null;
  displayName: string;
  tokens: number;
  /** Per-seat allocation. EMPTY-equivalent (all zero, uncommitted)
   * for seats that haven't allocated yet this match. */
  allocation: WheelStakeAllocation;
  online: boolean;
  /** Cell indices (0..14, row-major across the 5×3 grid) where
   * bonus tokens landed on this seat's most recent REEL_RACE_SPIN.
   * Empty list outside the REEL_RACE phase. Length 0–3, matching
   * the per-spin token award. */
  lastSpinBonusCells: number[];
  /** 15-cell row-major grid of slot symbol ids for the seat's most
   * recent REEL_RACE_SPIN. Cells listed in {@link lastSpinBonusCells}
   * carry the special id `"bonus"`; others carry one of the theme's
   * regular symbol ids (cherry, lemon, orange, bell, star, seven,
   * gem, scatter, money). Empty list outside REEL_RACE. */
  lastSpinGrid: string[];
  /** Payline settlement for the seat's most recent REEL_RACE_SPIN.
   * The grid is rolled with the same weighted symbol roster the
   * standalone slot uses, then bonus cells overlay specific positions
   * as non-paying items, then SettleSpinService walks the paylines —
   * so 3+ matches on a payline produce real settlements here. The
   * client passes this through to the slot scene's `highlightWins`
   * for the standard win celebration. Null on seats that have never
   * spun, or when the SSE delta is from a non-REEL_RACE phase. */
  lastSpinSettlement: WheelSpinSettlement | null;
  /** Phase 2 — per-round dial-wheel stake for this seat. Null while
   *  the seat is in REEL_RACE or before {@code SET_STAKE} fires
   *  during STAKE. Locked at STAKE → WHEEL_REVEAL (null seats default
   *  to {@code floor(tokens / 2)} server-side). Cleared back to null
   *  at PAYOUT once the round settles. */
  pendingStake: number | null;
  /** Reel-race item drop: 0..14 cell index (row-major across the
   *  5×3 grid) where an item "landed" on this seat's most recent
   *  REEL_RACE_SPIN, or null if no item dropped that spin. The
   *  client overlays a star at that cell's screen position, lingers
   *  briefly, then reveals the specific item — feels like the item
   *  physically arrived on the reels. The specific item is the most
   *  recently appended entry in {@code loadout}. */
  lastSpinItemCell: number | null;
  /** Phase 4 — server-rolled 3-item loadout for the current match.
   *  Populated at REEL_RACE → STAKE; cleared at startNewMatch. The
   *  inventory tiles read this list directly. */
  loadout: ItemId[];
  /** Phase 4 — items the seat has already deployed (via DEPLOY_ITEM)
   *  this round. Members appear in {@code loadout}; a duplicate item
   *  in the loadout can be deployed twice but never beyond the
   *  rolled count. */
  pendingDeployments: ItemId[];
  /** Reel-race claim: when a drop lands but the loadout is full (3),
   *  the item shows up here instead of being appended. The owning
   *  player must issue `CLAIM_ITEM` with a slotIndex (0..2) to swap
   *  it into the loadout; bots auto-claim FIFO. Null when no claim
   *  is pending (the common case). */
  lastSpinPendingItem: ItemId | null;
}

/** Payline-settlement payload mirroring the engine's
 *  {@code SpinSettlement} shape. Sent to the client per Reel Race
 *  spin so the slot scene's `highlightWins` can fire on real wins. */
export interface WheelSpinSettlement {
  totalWinnings: number;
  bet: number;
  net: number;
  settlements: ReadonlyArray<WheelLineSettlement>;
  scatter: WheelScatterSettlement | null;
}

export interface WheelLineSettlement {
  lineId: string;
  matchSymbolId: string;
  matchCount: number;
  hadWild: boolean;
  winnings: number;
}

export interface WheelScatterSettlement {
  scatterCount: number;
  matchSymbolId: string;
  winnings: number;
  positions: ReadonlyArray<{ col: number; row: number }>;
}

/** Four-way pool split for the current match (coins). */
export interface WheelPools {
  floor: number;
  prize: number;
  jackpot: number;
  houseFee: number;
  jackpotCarryover: number;
}

/** Outcome of one sub-spin during the legacy WHEEL phase. Only
 *  present on matches resolved through the retired three-sub-spin
 *  flow; new matches use {@link WheelResolveResult} instead. */
export interface WheelSubSpinResult {
  subSpin: WheelSubSpinKind;
  landedSliceOwner: number;
  rolledOver: boolean;
  /** seatId → coin payout. Empty when rolledOver=true. */
  payouts: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Dial wheel (Phase 1 — wheel-mechanic redesign)
// ---------------------------------------------------------------------------

/**
 * Kind of wedge on the 24-wedge spatial dial. {@code COINS} carries a
 * multiplier; the other four kinds are special-effect wedges with
 * resolution wired in Phase 5.
 *
 * <ul>
 *   <li>{@code COINS} — pays {@code stake × multiplier}; multiplier
 *       can be {@code 0} (a "lose your stake" outcome with no special
 *       effect).
 *   <li>{@code BANKRUPT} — seat loses its entire stake this round.
 *   <li>{@code SKIP_NEXT} — seat skips next round's stake/deploy.
 *   <li>{@code EXTRA_ITEM} — seat rolls a 4th item next loadout.
 *   <li>{@code SPECIAL_PRIZE} — seat's next-round stake gets a ×2
 *       multiplier ceiling.
 * </ul>
 */
export type WheelWedgeKind =
  | 'COINS'
  | 'BANKRUPT'
  | 'SKIP_NEXT'
  | 'EXTRA_ITEM'
  | 'SPECIAL_PRIZE';

/** One wedge on the 24-wedge dial. */
export interface WheelWedge {
  kind: WheelWedgeKind;
  /** Coin multiplier — present only when {@code kind === 'COINS'}. */
  multiplier?: number;
}

/**
 * Round-N dial composition. Eight variants ship in
 * {@code WheelRoundVariants} on the server; rounds 1–6 favour mild
 * COINS wedges, rounds 7–8 mix in BANKRUPT and special-effect wedges
 * to ratchet jeopardy.
 *
 * <p>Must contain exactly 24 wedges (one per 15° slice).
 */
export interface WheelDial {
  roundNumber: number;
  wedges: ReadonlyArray<WheelWedge>;
}

/** Per-seat outcome on the dial spin — which wedge their pointer
 *  read, plus the resolved payout and any deployed items that
 *  influenced the read. Phase 1 only computes payouts for COINS
 *  wedges; BANKRUPT and effect-wedge resolution lands in Phase 5. */
export interface WheelDialOutcome {
  /** 0..23 — which wedge the seat's pointer landed on, AFTER any
   *  applied items shifted the read (BOOST = +1 peg toward
   *  high-value wedge). */
  wedgeIndex: number;
  /** 0..23 — which wedge the seat's pointer WOULD have read before
   *  any item shifts. Equals {@code wedgeIndex} when no shifting
   *  items fired. Lets the client animate the wheel/pointer FROM the
   *  base wedge TO the boosted wedge after the spin lands so the
   *  player sees the +1-peg nudge happen rather than just reading
   *  "+1 PEG · BOOST" in the chip post-fact. */
  baseWedgeIndex: number;
  wedge: WheelWedge;
  /** Coin payout for this seat. Always 0 for non-COINS wedges in
   *  Phase 1; the eventual BANKRUPT math (negative payout = lose
   *  stake) lands in Phase 5. */
  payout: number;
  /** Phase 4 — items from {@code seat.pendingDeployments} that the
   *  resolver consumed when computing this outcome. Empty when no
   *  items fired. Mobile uses this to render the {@code +1 PEG}
   *  badge next to the player's pointer on a BOOST land. */
  appliedItems: ItemId[];
  /** Phase 5 — Mario-Kart-style item drop. When a seat lands on an
   *  EXTRA_ITEM wedge the resolver picks a deterministic item from
   *  the 20-item pool; the grant is appended to the seat's loadout at
   *  PAYOUT. Null on every other wedge kind. */
  grantedItem?: ItemId | null;
}

/**
 * Outcome of the spatial WHEEL_RESOLVE phase. The wheel lands at one
 * deterministic angle ({@code landingAngleDeg}); each seat reads its
 * own pointer-offset wedge ({@code perSeatOutcomes}).
 *
 * <p>Star Player wiring (round-7 announcement) is layered on later via
 * a separate {@code state.starPlayerSeatId} field — not part of the
 * resolve result itself.
 */
export interface WheelDialResolveResult {
  /** 0..360 — pointer-at-0° lands here at end of spin. */
  landingAngleDeg: number;
  /** 0..23 — wedge under the 0° pointer at landing time. */
  landingWedgeIndex: number;
  /** Round-N dial composition replayed back to the client so the
   *  reveal/resolve UI knows the wedge layout that produced this
   *  outcome. Avoids the client having to re-derive from
   *  {@code matchNumber}. */
  dial: WheelDial;
  /** seatId (string) → per-seat outcome. Every non-empty seat
   *  receives an entry. */
  perSeatOutcomes: Record<string, WheelDialOutcome>;
  /** Ordered "what just happened" sequence — one card per
   *  interactive item deploy this round (BOOST, HIT, SHIELD-as-
   *  blocking-flag-on-HIT). The client plays them as brief flashes
   *  between the wheel landing and the per-seat outcome chips so
   *  every player sees the story of how the wheel resolved.
   *  Self-only items (X2_BONUS, COIN_STASH, etc.) never appear
   *  here — those stay private to the affected player. Empty when
   *  no interactive items fired this round. */
  actionCards: WheelActionCard[];
  /** Producer brief 2026-05-15 — the landing angle the wheel
   *  WOULD have settled on with no MAGNET deployments. When this
   *  matches {@link landingAngleDeg} the spin had no magnet pull;
   *  when it differs, the client animates the wheel to this
   *  baseline first, holds briefly, then plays a "magnet pull"
   *  tween onto {@link landingAngleDeg}. */
  baselineLandingAngleDeg: number;
  /** Producer feedback 2026-05-27: <i>"I imagine the final winning
   *  ledger comes from the server."</i>
   *
   *  Per-seat post-spin payout breakdown — keyed by seatId as a
   *  string (mirrors the {@code perSeatOutcomes} encoding so JSON
   *  keys remain stable). Wedge-baked items (MAGNET/BOOST/
   *  TRIPLE_BOOST/X2_BONUS) are filtered out server-side because
   *  their effect is already reflected in the seat's
   *  {@link WheelDialOutcome#payout}. Empty list for seats with
   *  no attributable changes; missing entry for empty seats. */
  ledgerLinesBySeat: Record<string, ReadonlyArray<WheelLedgerLine>>;
}

/** One row in a seat's post-spin payout ledger. Structured rather
 *  than pre-rendered so the client can localise + restyle labels
 *  without a server roll. See server-side
 *  {@code WheelLedgerLine.java} for the canonical model. */
export interface WheelLedgerLine {
  /** {@code 'INCOMING'} = local seat took damage from {@link otherSeatId}.
   *  {@code 'OUTGOING'} = local seat fired this card at {@link otherSeatId}.
   *  {@code 'SELF'} = self-effect / mass-attack summary on the deployer. */
  kind: 'INCOMING' | 'OUTGOING' | 'SELF';
  /** Drives the row icon + label prefix on the client. */
  itemId: ItemId;
  /** seatId of the attacker (INCOMING) or victim (OUTGOING);
   *  {@code null} for SELF. */
  otherSeatId: number | null;
  /** Signed payout-units change for the local seat. May be 0 only
   *  when {@link blocked} is true. */
  delta: number;
  /** True for sabotage absorbed by a SHIELD on the local seat —
   *  client dims the row and shows "Blocked" instead of "+0". */
  blocked: boolean;
}

/**
 * One beat in the post-spin "what just happened" sequence — each
 * interactive item deploy gets a card so every player sees the
 * story of how the wheel resolved. Producer brief 2026-05-13: the
 * dynamic of each player's special items being able to affect one
 * another is half the fun.
 */
export interface WheelActionCard {
  /** Which item drove this beat. v1 emits BOOST and HIT. */
  itemId: ItemId;
  /** seatId of the player who deployed the item. */
  sourceSeatId: number;
  /** seatId of the target, or {@code null} for self-buffs (BOOST
   *  shifts the deployer's own pointer; there's no target seat). */
  targetSeatId: number | null;
  /** True when this card represents a HIT that the target absorbed
   *  with a SHIELD. The card still plays — it's the "Player X HIT
   *  Player Y · BLOCKED!" beat that gives the defender their
   *  moment. */
  blocked: boolean;
  /** Signed payout-units change applied to the SOURCE seat. BOOST
   *  adds the wedge-shift gain; SHAKEDOWN adds the transferred
   *  amount; X2_BONUS adds the doubled amount; COIN_STASH adds 50.
   *  0 when this card doesn't affect the deployer's pot (SHIELD,
   *  BLOCKED HIT/BOMB). Drives the OutcomeChip's itemised
   *  breakdown — "+60 from steal", "+50 from coin stash", etc. */
  sourceDelta: number;
  /** Signed payout-units change applied to the TARGET seat. HIT
   *  /BOMB carry the wedge-shift loss; LIGHTNING carries −base/2;
   *  SHAKEDOWN carries the negative of {@code sourceDelta}; BLOCKED
   *  cards carry 0. 0 when {@code targetSeatId} is null. */
  targetDelta: number;
}

/** Sealed-envelope hash for round-end verification (32-byte SHA-256
 * hex). Present from CHARGING onwards; null during IDLE. */
export interface RoundSealedHash {
  hex: string;
}

/**
 * Top-level SSE payload — the role-filtered projection of a Wheel
 * room's state, broadcast on {@code state_init} and patched in via
 * RFC-6902 deltas on {@code state_delta}.
 */
export interface WheelObservableState {
  version: number;
  phase: WheelPhase;
  role: ObserverRole;
  seats: ReadonlyArray<WheelSeatView>;
  meter: number;
  /** Match number within the room's lifetime (1-indexed). Null in IDLE. */
  matchNumber: number | null;
  pools: WheelPools;
  /** Legacy three-sub-spin results (FLOOR / PRIZE / JACKPOT). Always
   *  empty under the placement-driven flow; populated only for matches
   *  that ran the retired WHEEL phase. */
  subSpinResults: ReadonlyArray<WheelSubSpinResult>;
  /** seatId (string) → cheer count from this room's audience. */
  fanFavoriteCheers: Record<string, number>;
  /** Null during IDLE. */
  matchSealedHash: RoundSealedHash | null;
  /** F1.next — count of distinct AUDIENCE-role SSE subscribers
   *  currently watching this room. Stamped server-side by
   *  SseRoomBroadcaster on every state emission (init + delta).
   *  Drives the client's "X watching" indicator. */
  audienceCount: number;
  /** Spatial 24-wedge resolve outcome populated at WHEEL_RESOLVE
   *  under the new dial-wheel flow. Null outside the WHEEL_RESOLVE
   *  → PAYOUT span. */
  dialResolveResult: WheelDialResolveResult | null;
  /** Round-N dial composition. Populated at STAKE entry so the stake
   *  slider can preview the round's risk profile, and stays through
   *  WHEEL_REVEAL / WHEEL_RESOLVE / PAYOUT so the prize board and
   *  spinning wheel can render the same wedges. Null on
   *  IDLE / CHARGING / REEL_RACE. */
  currentDial: WheelDial | null;
  /** seatId (string) → pointer slot (1..6) on the wheel for the
   *  current round. Defaults to the identity mapping; players can
   *  swap slots via {@code SELECT_SEAT} during STAKE. The map ALWAYS
   *  covers every seat 1..6 (the swap keeps the invariant) so the
   *  client can read each seat's slot without falling back. */
  seatPointerSlots: Record<string, number>;
  /** True iff the server is holding the room in pause-IDLE because
   *  every human seat has left or timed out. The room won't kick
   *  off a new match until either a fresh JOIN_ROOM arrives or the
   *  idle sweeper releases the actor. The mobile lobby reads this
   *  to auto-rejoin a returning player who was reclaimed by the
   *  heartbeat sweeper — without it, the client would sit on the
   *  prep loader forever waiting for a CHARGING that never starts. */
  paused: boolean;
  /** Bonus-wedge placements committed by players via DEPLOY_ITEM
   *  BONUS_WEDGE during the current STAKE phase. The client renders
   *  each placement on the dial in STAKE so the deployer (and
   *  rivals) see the bonus wedge land immediately — not only at
   *  WHEEL_RESOLVE. Producer brief 2026-05-15. Empty outside
   *  STAKE → WHEEL_RESOLVE. */
  bonusWedgePlacements: ReadonlyArray<BonusWedgePlacement>;
  /** Producer feedback 2026-05-27 — step-by-step debug mode. When
   *  true, every auto-advancer on the server is paused (scheduled
   *  phase transitions are routed into a debug queue; bots skip
   *  their tick). The mobile client renders a small "PAUSED ·
   *  MANUAL MODE" pill so the human producer knows the room is
   *  frozen. Defaults to false in production. */
  manualMode: boolean;
}

/**
 * One bonus-wedge drop placed on the current round's dial by a
 * player's DEPLOY_ITEM BONUS_WEDGE command. Mirrors
 * com.pixebyte.goodtime.domain.model.roomengine.wheel.BonusWedgePlacement.
 */
export interface BonusWedgePlacement {
  /** seatId (string-encoded long) that fired the DEPLOY_ITEM. */
  sourceSeatId: string;
  /** 0..23 dial slot the bonus replaces this round. */
  wedgeIndex: number;
}

/** Display copy + accent for each phase pill. UX Spec §5.2. */
export const WHEEL_PHASE_META: Record<
  WheelPhase,
  { label: string; accent: string; durationSec: number | null }
> = {
  IDLE:          { label: 'Resting',         accent: '#7E8DA8', durationSec: 30 },
  CHARGING:      { label: 'Charging',        accent: '#6BCBFF', durationSec: 60 },
  REEL_RACE:     { label: 'Reel Race',       accent: '#FFC940', durationSec: 60 },
  STAKE:         { label: 'Stake',           accent: '#FF9D40', durationSec: 30 },
  WHEEL_REVEAL:  { label: 'Bonus Reveal',    accent: '#E8344E', durationSec: 8 },
  WHEEL_RESOLVE: { label: 'Star Player',     accent: '#FF6F6F', durationSec: 12 },
  WHEEL:         { label: 'Wheel Spin',      accent: '#E8344E', durationSec: 45 }, // legacy
  PAYOUT:        { label: 'Payout',          accent: '#9DE54E', durationSec: 6 }, // mirrors server PAYOUT_DURATION_MS (6_000ms); decorative — does NOT gate the wrap
};

/** SSE event names broadcast by the server. */
export type WheelSseEvent = 'state_init' | 'state_delta' | 'close';

/**
 * Match-end podium multiplier — PAYOUT (this match's dial winnings)
 * is multiplied by {@code 1 + percent/100} for each top-3 seat by
 * settled tokens. Index 0 = 1st (+100% → ×2), 1 = 2nd (+50% → ×1.5),
 * 2 = 3rd (+20% → ×1.2). Producer brief 2026-05-16: "the top three
 * spots get a winnings multiplier, so it's actually something worth
 * competing for." Server reads from
 * {@code WheelGameRules.PODIUM_MULTIPLIER_PERCENT} — the two MUST
 * stay in sync; the client uses this array to label the podium chip
 * (`×2` / `×1.5` / `×1.2`) on the payout standings.
 */
export const WHEEL_PODIUM_MULTIPLIER_PERCENT: ReadonlyArray<number> = [100, 50, 20];

/**
 * Human-friendly multiplier label per podium rank. Derived from
 * {@link WHEEL_PODIUM_MULTIPLIER_PERCENT}: rank 1 → "×2",
 * rank 2 → "×1.5", rank 3 → "×1.2". Trailing zeros are stripped so
 * "×1.20" reads as "×1.2".
 */
export function wheelPodiumMultiplierLabel(rank: number): string {
  const percent = WHEEL_PODIUM_MULTIPLIER_PERCENT[rank - 1];
  if (percent == null) return '';
  const multiplier = 1 + percent / 100;
  const trimmed = multiplier
    .toFixed(2)
    .replace(/\.?0+$/, '');
  return `×${trimmed}`;
}

/**
 * Total matches that make up one wheel-room session. Producer brief
 * 2026-05-16 — the room used to loop matches forever, which read as
 * "endless rounds" in felt-test. After this many matches the room
 * resets: seat tokens + loadouts clear and the match counter
 * restarts at 1. Mirror of {@code WheelGameRules.MATCHES_PER_SESSION}
 * — keep the two in sync. Lets the client render "Match N of M".
 */
export const WHEEL_MATCHES_PER_SESSION = 5;

// MK-style "Question Box" loadout system — replaces the Stake allocator.
// See ./items.ts and plans/gear-up-for-the-purrfect-biscuit.md.
export * from './items';


/** Every slot's fixed bet B: what one paid spin costs (Decision 2, one maths
 *  for every slot). Mirrors the server's {@code app.slots}; the server plays
 *  its own value whatever the app sends. */
export const SLOT_DEFAULT_BET = 25;
export const SLOT_BETS: Readonly<Record<string, number>> = { royal: 50 };

export function slotBetFor(themeId: string | null | undefined): number {
  return (themeId ? SLOT_BETS[themeId] : undefined) ?? SLOT_DEFAULT_BET;
}

/** The bets a player can choose on a slot: its bet B times these (the
 *  server's {@code app.slots.multiples}). A bigger bet moves the trail, the
 *  road's side lights and its pots faster; prizes stay sized to B. */
export const SLOT_BET_MULTIPLES: readonly number[] = [1, 2, 4, 8];

export function slotBetLadder(themeId: string | null | undefined): number[] {
  const base = slotBetFor(themeId);
  return SLOT_BET_MULTIPLES.map((m) => m * base);
}

/** The reels' pay scale on the server's {@code rr_v1} maths
 *  ({@code SlotConfigCatalog.RR_V1_PAY_SCALE}): a line pays
 *  max(1, round(bet x multiplier x scale)). */
export const RR_V1_PAY_SCALE = 0.077;
